import React, { useState } from 'react';
import { AlertTriangle, Check, ChevronDown, DatabaseZap } from 'lucide-react';
import Button from '../UI/Button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../UI/alert-dialog';
import { adminSettingsAPI } from '../../services/api';
import { logError, logInfo } from '../../utils/logger';

const CACHE_PATTERN_OPTIONS = [
  'memory:df:*',
  'chat_v2:odata:v2:data:*'
] as const;

const errorMessage = (err: any, fallback: string): string => (
  err?.response?.data?.detail || err?.response?.data?.message || fallback
);

export const AdminOperationsTab: React.FC = () => {
  const [cachePattern, setCachePattern] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [cacheMessage, setCacheMessage] = useState<string | null>(null);
  const [cacheError, setCacheError] = useState<string | null>(null);

  const requestClearCache = () => {
    const pattern = cachePattern.trim();
    if (!pattern) {
      setCacheError('Select a cache pattern.');
      return;
    }
    setCacheError(null);
    setConfirmOpen(true);
  };

  const clearCache = async () => {
    const pattern = cachePattern.trim();
    if (!pattern) return;
    try {
      setClearingCache(true);
      setCacheError(null);
      setCacheMessage(null);
      const response = await adminSettingsAPI.clearCache({ pattern });
      setCacheMessage(`Cleared ${response.data.deleted_count} cache entr${response.data.deleted_count === 1 ? 'y' : 'ies'} for ${response.data.pattern}.`);
      logInfo('AdminOperationsTab', 'Cache cleared by pattern', response.data);
    } catch (err: any) {
      logError('AdminOperationsTab', 'Failed to clear cache', err);
      setCacheError(errorMessage(err, 'Failed to clear cache.'));
    } finally {
      setClearingCache(false);
      setConfirmOpen(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Cache Settings</h2>
        <p className="mt-1 text-slate-600">
          Clear server cache entries by Redis key pattern.
        </p>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-6">
        <div className="flex items-start gap-3">
          <DatabaseZap className="mt-1 h-5 w-5 text-primary-600" />
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-slate-900">Clear Cache</h3>
            <p className="mt-1 text-sm text-slate-600">
              Select the cache pattern to clear.
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-col gap-3 sm:flex-row">
          <div className="min-w-0 flex-1">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Cache pattern
            </label>
            <div
              className="relative"
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                  setDropdownOpen(false);
                }
              }}
            >
              <button
                type="button"
                aria-haspopup="listbox"
                aria-expanded={dropdownOpen}
                onClick={() => setDropdownOpen((open) => !open)}
                className={`flex h-11 w-full items-center justify-between rounded-md border bg-white px-3 py-2 text-left font-mono text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-100 ${
                  dropdownOpen
                    ? 'border-primary-400'
                    : 'border-slate-300 hover:border-slate-400'
                }`}
              >
                <span className={cachePattern ? 'text-slate-900' : 'text-slate-400'}>
                  {cachePattern || 'Select cache pattern'}
                </span>
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-slate-500 transition-transform duration-150 ${
                    dropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>
              <div
                role="listbox"
                className={`absolute left-0 right-0 top-full z-40 mt-1 origin-top rounded-md border border-slate-200 bg-white p-1 shadow-lg transition-all duration-150 ease-out ${
                  dropdownOpen
                    ? 'translate-y-0 scale-100 opacity-100'
                    : 'pointer-events-none -translate-y-1 scale-95 opacity-0'
                }`}
              >
                {CACHE_PATTERN_OPTIONS.map((pattern) => (
                  <button
                    key={pattern}
                    type="button"
                    role="option"
                    aria-selected={cachePattern === pattern}
                    onClick={() => {
                      setCachePattern(pattern);
                      setDropdownOpen(false);
                    }}
                    className="flex w-full items-center justify-between rounded px-3 py-2 text-left font-mono text-sm text-slate-800 transition-colors hover:bg-primary-50 hover:text-primary-700 focus:bg-primary-50 focus:outline-none"
                  >
                    <span>{pattern}</span>
                    {cachePattern === pattern && <Check className="h-4 w-4 text-primary-600" />}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <Button
            type="button"
            onClick={requestClearCache}
            disabled={clearingCache || !cachePattern}
            className="shrink-0 sm:mt-6"
          >
            <DatabaseZap className="h-4 w-4" />
            {clearingCache ? 'Clearing...' : 'Clear cache'}
          </Button>
        </div>

        {cacheMessage && (
          <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            {cacheMessage}
          </div>
        )}
        {cacheError && (
          <div className="mt-4 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <AlertTriangle className="h-4 w-4" />
            {cacheError}
          </div>
        )}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={(open) => {
        if (!clearingCache) setConfirmOpen(open);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear selected cache?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete every Redis key matching{' '}
              <span className="font-mono font-semibold text-slate-900">{cachePattern}</span>.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={clearingCache}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void clearCache();
              }}
              disabled={clearingCache}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {clearingCache ? 'Clearing...' : 'Confirm clear'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
